import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { profileApi } from '../services/api';

const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation('app');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const getPasswordStrength = (password: string): number => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 8) s++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) s++;
    if (/\d/.test(password)) s++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) s++;
    return Math.min(s, 4);
  };

  const strength = getPasswordStrength(formData.newPassword);
  const strengthColors = ['bg-gray-200 dark:bg-gray-600', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) { alert(t('changePassword.mismatch')); return; }
    if (formData.newPassword.length < 6) { alert(t('changePassword.tooShort')); return; }
    setLoading(true);
    try {
      await profileApi.changePassword({ currentPassword: formData.currentPassword, newPassword: formData.newPassword });
      alert(t('changePassword.success'));
      setTimeout(() => navigate('/profile'), 1000);
    } catch (error: any) {
      alert(error.response?.data?.message || t('changePassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent';
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <KeyIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">{t('changePassword.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('changePassword.subtitle')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t('changePassword.currentPassword')}</label>
            <input type="password" value={formData.currentPassword} onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>{t('changePassword.newPassword')}</label>
            <input type="password" value={formData.newPassword} onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })} className={inputCls} required />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('changePassword.newPasswordHint')}</p>
          </div>

          {formData.newPassword && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('changePassword.strength')}</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={`h-2 flex-1 rounded-full ${i <= strength ? strengthColors[strength] : 'bg-gray-200 dark:bg-gray-600'}`} />
                ))}
              </div>
              {strength > 0 && <p className="text-sm text-gray-600 dark:text-gray-400">{t(`changePassword.strengthLevels.${strength}`)}</p>}
            </div>
          )}

          <div>
            <label className={labelCls}>{t('changePassword.confirmPassword')}</label>
            <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className={inputCls} required />
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              {t('changePassword.cancel')}
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 cursor-pointer">
              {loading ? t('changePassword.submitting') : t('changePassword.submit')}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">{t('changePassword.tips.title')}</h3>
            <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1">
              <li>• {t('changePassword.tips.1')}</li>
              <li>• {t('changePassword.tips.2')}</li>
              <li>• {t('changePassword.tips.3')}</li>
              <li>• {t('changePassword.tips.4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
